
import Groq from "groq-sdk";
import { AppData, AIConfig, LeadStatus, KnowledgeItem } from '../types';

// --- AI ROUTER & BRAIN ---

const DEFAULT_GROQ_KEY = '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';
const DEFAULT_GROQ_MODEL = "llama-3.3-70b-versatile";

// 1. Provider Factory
const getAICompletion = async (
    config: AIConfig, 
    messages: any[], 
    temperature = 0.5,
    jsonMode = false
): Promise<string> => {
    
    // Always attempt to fetch. No local blocking.

    const provider = config.activeProvider;
    const providerConfig = config.providers[provider];
    
    // Safety check - if provider not configured, use default Groq
    let apiKey = DEFAULT_GROQ_KEY;
    let model = DEFAULT_GROQ_MODEL;

    if (provider === 'groq' && providerConfig && providerConfig.enabled) {
        apiKey = providerConfig.apiKey || DEFAULT_GROQ_KEY;
        model = providerConfig.model || DEFAULT_GROQ_MODEL;
    } else if (provider !== 'groq') {
        if (providerConfig && providerConfig.enabled && providerConfig.apiKey) {
            // For now, if other providers are selected but not fully implemented, we mock or fallback
            // But based on user code, we fallback to Groq logic for this specific setup
            return "AI Provider Integration Pending (Mock Response)."; 
        }
    }

    try {
        return await callGroq(apiKey, model, messages, temperature, jsonMode);
    } catch (e: any) {
        console.error("AI Provider Error:", e);
        // Handle Rate Limiting gracefully
        if (e?.error?.code === 'rate_limit_exceeded' || e?.status === 429) {
             if (jsonMode) return JSON.stringify({ text: "RATE_LIMIT_HIT", action: null });
             throw new Error("RATE_LIMIT");
        }
        // Simply throw error so individual functions can handle it (retry or show error message)
        throw new Error("AI_CONNECTION_FAILED");
    }
};

// 2. Groq Implementation
const callGroq = async (apiKey: string, model: string, messages: any[], temperature: number, jsonMode: boolean) => {
    try {
        const groq = new Groq({ apiKey: apiKey, dangerouslyAllowBrowser: true });
        
        const params: any = {
            messages: messages,
            model: model,
            temperature: temperature,
            max_tokens: 1024
        };

        if (jsonMode) {
            params.response_format = { type: "json_object" };
        }

        const completion = await groq.chat.completions.create(params);
        return completion.choices[0]?.message?.content || "";
    } catch (error: any) {
        // Re-throw to be caught by getAICompletion
        throw error;
    }
};

// Helper: Build Knowledge Context
const generateKnowledgeContext = (kb: KnowledgeItem[]): string => {
    if (!kb || kb.length === 0) return "";
    
    return `
    RELEVANT KNOWLEDGE BASE (USE THIS TO ANSWER USER):
    ${kb.map(item => `
    --- DOCUMENT: ${item.name} ---
    ${item.content}
    -----------------------------
    `).join('\n')}
    `;
};

// --- FALLBACK: SMART SIMULATOR ---
// This runs when AI is down or rate limited.
const getSimulatedResponseLogic = (lastMessage: string): { text: string, action: 'menu' | 'contact' | 'lead_form' | null } => {
    const lowerMsg = lastMessage.toLowerCase();
    
    // 1. Phone Number Detection (Regex for various formats)
    const phoneRegex = /(\+91[\-\s]?)?[0]?(91)?[6789]\d{9}/;
    if (phoneRegex.test(lastMessage)) {
        return {
            text: "Thanks for sharing your contact! Our team will call you shortly to discuss your event. Would you like to see the menu in the meantime?",
            action: 'menu'
        };
    }

    // 2. Menu Request
    if (lowerMsg.includes('menu') || lowerMsg.includes('food') || lowerMsg.includes('items') || lowerMsg.includes('list') || lowerMsg.includes('dish')) {
        return {
            text: "We offer a wide variety of South Indian, North Indian, and Chinese delicacies. Prices start at ₹250/plate. Check out our menu here:",
            action: 'menu'
        };
    }

    // 3. Pricing / Cost -> TRIGGER LEAD FORM (Convince them)
    if (lowerMsg.includes('price') || lowerMsg.includes('cost') || lowerMsg.includes('rate') || lowerMsg.includes('charge') || lowerMsg.includes('much') || lowerMsg.includes('quote')) {
        return {
            text: "Our packages start from ₹250/plate. To give you an exact quote based on your guest count and date, please enter your details below for an instant estimate:",
            action: 'lead_form'
        };
    }

    // 4. Booking / Specific Event Types -> TRIGGER LEAD FORM
    if (lowerMsg.includes('wedding') || lowerMsg.includes('reception') || lowerMsg.includes('birthday') || lowerMsg.includes('book') || lowerMsg.includes('date') || lowerMsg.includes('plan')) {
         return {
            text: "That sounds like a wonderful event! Let me check our availability for your date. Please fill out this quick form:",
            action: 'lead_form'
        };
    }

    // 5. Contact Info
    if (lowerMsg.includes('call') || lowerMsg.includes('contact') || lowerMsg.includes('number') || lowerMsg.includes('location') || lowerMsg.includes('address') || lowerMsg.includes('where')) {
        return {
            text: "We are located at CBI Road, Ganganagar, Bangalore. You can call us directly or click below to connect on WhatsApp.",
            action: 'contact'
        };
    }

    // 6. Greetings
    if (lowerMsg.includes('hi') || lowerMsg.includes('hello') || lowerMsg.includes('hey') || lowerMsg.includes('good') || lowerMsg.includes('start')) {
        return {
            text: "Hello! Welcome to CaterMax. Are you looking to get a quote for an upcoming event?",
            action: null
        };
    }

    // 7. Default Fallback -> Nudge to Form
    return {
        text: "I'd love to help plan your event. To get started with pricing and availability, please share a few details:",
        action: 'lead_form'
    };
};

// --- CRM INTELLIGENCE ---

// 3. Lead Classifier
export const analyzeLeadChat = async (
    chatHistory: { role: 'user' | 'ai'; text: string }[],
    config: AIConfig
): Promise<{ status: LeadStatus; score: number; reason: string; summary: string }> => {
    
    const systemPrompt = `
    You are the Lead Quality Analyst for CaterMax.
    Your job is to analyze the chat conversation and classify the lead.
    
    CRITERIA:
    - HOT (Score 80-100): Ready to book, urgent, budget clear, sharing contact details.
    - WARM (Score 40-79): Asking prices, dates, general inquiries, but vague.
    - COLD (Score 0-39): Just browsing, spam, or irrelevant.

    INSTRUCTIONS:
    1. Look for Phone Numbers. If phone is shared -> Automatic HOT.
    2. Look for Date/Guest Count. Specifics -> WARM/HOT.
    3. Look for Budget. Discussing money -> WARM/HOT.
    
    OUTPUT JSON FORMAT ONLY:
    {
        "status": "HOT" | "WARM" | "COLD",
        "score": number,
        "reason": "Short explanation",
        "summary": "2 sentence summary of user request"
    }
    `;

    const messages = [
        { role: "system", content: systemPrompt },
        { role: "user", content: JSON.stringify(chatHistory) }
    ];

    try {
        const result = await getAICompletion(config, messages, 0.1, true);
        const parsed = JSON.parse(result);
        return {
            status: parsed.status || 'COLD',
            score: parsed.score || 0,
            reason: parsed.reason || "Insufficient data",
            summary: parsed.summary || "No summary"
        };
    } catch (e) {
        // Return null/default on error, but don't block future requests
        return { status: 'WARM', score: 50, reason: "Analysis Skipped (System Busy)", summary: "Auto-captured." };
    }
};

// 4. Chatbot Response Logic
export const getChatbotResponse = async (
    history: { role: 'user' | 'ai'; text: string }[],
    newMessage: string,
    config: AIConfig,
    knowledgeBase: KnowledgeItem[]
): Promise<{ text: string, action: 'menu' | 'contact' | 'lead_form' | null }> => {
    
    const kbContext = generateKnowledgeContext(knowledgeBase);
    
    // Merge the user-defined global prompt with required JSON technical instructions
    const basePrompt = config.systemCommands || "You are a helpful assistant.";

    const systemCommand = `
    ${basePrompt}
    
    --- TECHNICAL INSTRUCTIONS (INTERNAL) ---
    You MUST output your response in valid JSON format.
    Structure: { "text": "Your reply string here", "action": "menu" | "contact" | "lead_form" | null }
    
    Action Logic:
    - "lead_form": HIGH PRIORITY. Trigger this if the user asks about Price, Quote, Availability, Booking, or gives details like guest count/date. Frame it as "Check Availability" or "Get Instant Quote".
    - "contact": Use only if they specifically ask for phone number/location or if conversation is going in circles.
    - "menu": If user asks for menu.
    - null: For general greetings or small talk.

    Do not include markdown formatting (like \`\`\`json). Just the raw JSON object.
    
    ${kbContext}
    `;
    
    const messages = [
        { role: "system", content: systemCommand },
        ...history.map(h => ({ role: h.role === 'ai' ? 'assistant' : 'user', content: h.text })),
        { role: "user", content: newMessage }
    ];

    try {
        const result = await getAICompletion(config, messages, 0.7, true);
        const parsed = JSON.parse(result);
        
        // Check for specific Rate Limit signal from getAICompletion
        if (parsed.text === "RATE_LIMIT_HIT") {
            throw new Error("RATE_LIMIT");
        }

        return {
            text: parsed.text || "How can I help you today?",
            action: parsed.action || null
        };
    } catch (e: any) {
        console.warn("AI Generation Failed (" + e.message + "). Switching to Smart Simulator.");
        // Fallback to Smart Simulator
        return getSimulatedResponseLogic(newMessage);
    }
};

// 5. Gmail Automation (Simulated)
export const triggerEmailAutomation = async (leadData: any, config: AIConfig) => {
    if (!config.emailSettings.enabled || leadData.status !== 'HOT') return;
    
    // Simulation only
    return new Promise(resolve => setTimeout(resolve, 1000));
};


// --- FINANCE INTELLIGENCE ---

export const getFinancialSummary = async (data: AppData, forceRefresh = false): Promise<string> => {
   const context = generateComprehensiveContext(data);
   const messages = [
     { role: "system", content: "You are the CaterMax Finance Auditor. Analyze strictly." },
     { role: "user", content: `DATA:\n${context}\n\nTASK: Provide a strict financial summary (under 40 words).` }
   ];
   
   try {
       return await getAICompletion(data.aiConfig, messages, 0.1, false);
   } catch (e: any) {
       if (e.message === 'RATE_LIMIT') return "AI Analysis Paused (Rate Limit).";
       return "Financial Insights temporarily unavailable.";
   }
};

export const detectAnomalies = async (data: AppData, forceRefresh = false) => {
    const context = generateComprehensiveContext(data);
    const messages = [
        { role: "system", content: "You are a Forensic Accountant. Output strictly valid JSON with key 'items'." },
        { role: "user", content: `Data:\n${context}\n\nIdentify 3 specific anomalies.` }
    ];
    try {
        const res = await getAICompletion(data.aiConfig, messages, 0.1, true);
        const parsed = JSON.parse(res);
        return parsed.items || [];
    } catch(e) { return []; }
};

export const chatWithFinanceAssistant = async (
  history: { role: 'user' | 'ai', text: string }[], 
  newMessage: string, 
  data: AppData
): Promise<string> => {
  const context = generateComprehensiveContext(data);
  const messages: any[] = [
      { role: "system", content: `You are the CaterMax Database Assistant. Access DB below.\n${context}` },
      ...history.map(h => ({ role: h.role === 'ai' ? 'assistant' : 'user', content: h.text })),
      { role: "user", content: newMessage }
  ];
  
  try {
      return await getAICompletion(data.aiConfig, messages, 0.2, false);
  } catch (e: any) {
      if (e.message === 'RATE_LIMIT') return "System is busy. Please try again later.";
      return "Unable to process request. Please try again.";
  }
};


// Helper for Finance Context
const generateComprehensiveContext = (data: AppData): string => {
    const today = new Date().toISOString().split('T')[0];
    const eventsList = data.events.map(e => `ID:${e.id}|Name:${e.customerName}|Date:${e.date}|Status:${e.status}|Total:${e.finalTotal}`).join('\n');
    const totalRev = data.payments.reduce((sum, p) => sum + p.amount, 0);
    const totalExp = data.expenses.reduce((sum, e) => sum + e.amount, 0);
  
    return `
  CURRENT_DATE: ${today}
  DATABASE:
  [EVENTS]
  ${eventsList}
  [TOTALS]
  Income: ${totalRev} | Spend: ${totalExp}
  `;
};
